import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { refreshAuthentication, logoutUser } from '../store/slices/authSlice';
import { branding } from '../constants/branding';

interface InactivityModalProps {
  isVisible: boolean;
  onContinue: () => void;
  onLogout: () => void;
  timeRemaining: number;
}

const InactivityModal: React.FC<InactivityModalProps> = ({
  isVisible,
  onContinue,
  onLogout,
  timeRemaining
}) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const [isExtending, setIsExtending] = useState(false);
  const [countdown, setCountdown] = useState(timeRemaining);

  // Add debugging
  useEffect(() => {
    console.log('InactivityModal: Props received:', {
      isVisible,
      timeRemaining,
      isAuthenticated: auth.isAuthenticated
    });
  }, [isVisible, timeRemaining, auth.isAuthenticated]);

  useEffect(() => {
    if (isVisible && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            onLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isVisible, countdown, onLogout]);

  const handleContinue = async () => {
    setIsExtending(true);
    try {
      console.log('InactivityModal: Extending session...');
      await dispatch(refreshAuthentication()).unwrap();
      console.log('InactivityModal: Session extended successfully');
      onContinue();
    } catch (error) {
      console.error('InactivityModal: Failed to extend session:', error);
      onLogout();
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      console.error('InactivityModal: Logout error:', error);
    } finally {
      onLogout();
    }
  };

  if (!isVisible) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  // Prevent activity detection when interacting with modal
  const handleModalInteraction = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  };

  // Handle clicking outside the modal (treat as logout for security)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      handleLogout();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: branding.fontFamily
      }}
      onClick={handleBackdropClick}
      onMouseMove={handleModalInteraction}
      onKeyDown={handleModalInteraction}
    >
      <div 
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          maxWidth: 400,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          animation: 'slideIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
      >
        <style>
          {`
            @keyframes slideIn {
              from { 
                opacity: 0; 
                transform: translateY(-20px) scale(0.95); 
              }
              to { 
                opacity: 1; 
                transform: translateY(0) scale(1); 
              }
            }
          `}
        </style>

        {/* Icon */}
        <div style={{
          fontSize: 48,
          marginBottom: 16,
          color: branding.warningColor || '#f39c12'
        }}>
          ⏰
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 16px 0',
          color: branding.textColor,
          fontSize: 24,
          fontWeight: 700
        }}>
          Session Timeout Warning
        </h2>

        {/* Message */}
        <p style={{
          margin: '0 0 24px 0',
          color: '#666',
          fontSize: 16,
          lineHeight: 1.5
        }}>
          You've been inactive for 10 minutes. Your session will expire in{' '}
          <strong style={{ color: branding.errorColor }}>
            {minutes}:{seconds.toString().padStart(2, '0')}
          </strong>
          {' '}if you don't continue.
        </p>

        {/* Auth Type Info */}
        <div style={{
          background: '#f8f9fa',
          borderRadius: 8,
          padding: 12,
          margin: '0 0 24px 0',
          fontSize: 14,
          color: '#666'
        }}>
          <strong>Authentication Type:</strong> {auth.type === 'JWT' ? 'JWT Token' : 'Session Cookie'}
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: 12,
          justifyContent: 'center'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleContinue();
            }}
            disabled={isExtending}
            style={{
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: isExtending ? 'not-allowed' : 'pointer',
              opacity: isExtending ? 0.7 : 1,
              minWidth: 120
            }}
          >
            {isExtending ? (
              <>
                <span style={{ marginRight: 8 }}>⏳</span>
                Extending...
              </>
            ) : (
              <>
                <span style={{ marginRight: 8 }}>✅</span>
                Continue Session
              </>
            )}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLogout();
            }}
            disabled={isExtending}
            style={{
              background: 'transparent',
              color: branding.errorColor,
              border: `2px solid ${branding.errorColor}`,
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: isExtending ? 'not-allowed' : 'pointer',
              opacity: isExtending ? 0.7 : 1,
              minWidth: 120
            }}
          >
            <span style={{ marginRight: 8 }}>🚪</span>
            Logout
          </button>
        </div>

        {/* Countdown Warning */}
        {countdown <= 30 && (
          <div style={{
            marginTop: 16,
            padding: 8,
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: 6,
            color: '#856404',
            fontSize: 14
          }}>
            ⚠️ Session will expire soon!
          </div>
        )}

        {/* Click outside hint */}
        <div style={{
          marginTop: 12,
          fontSize: 12,
          color: '#999',
          fontStyle: 'italic'
        }}>
          Click outside to logout
        </div>
      </div>
    </div>
  );
};

export default InactivityModal; 